import React from "react";
import moment from "moment";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faHourglass,
  faUserCircle,
  faCheckCircle,
  faExclamationCircle
} from "@fortawesome/free-solid-svg-icons";

import request from "utils/request";

import { compressFile } from "client/customer/meta";

import { baseRequestURL } from "api/BaseAPI";

import AWSIcon from "images/EDG/DB/AWS.svg";
import AzureIcon from "images/EDG/DB/AZURE.svg";
import GCPIcon from "images/gcp.svg";

import { Button } from "aible";

import { validateFileExtension } from "containers/Upload/reducer";

import Logo from "images/logo1.png";
import AibleLogo from "images/Aible_Logo/Aible_Logo.png";

import styles from "./style.module.css";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { isAWSPresignedUrl, isAzurePresignedUrl, isGCPPresignedUrl } from "types/dataset";

function checkIsAWS(presignedUrl) {
  return isAWSPresignedUrl(presignedUrl?.presigned_url);
}

function checkIsAzure(presignedUrl) {
  return isAzurePresignedUrl(presignedUrl?.data_url);
}

function checkIsGCP(presignedUrl) {
  return isGCPPresignedUrl(presignedUrl?.data_url);
}

function handleFile(event, presignedUrl) {
  const file = (event.currentTarget.files ?? [])[0];

  if (file && validateFileExtension(file)) {
    return compressFile(file).then((compressed) => {
      if (checkIsAWS(presignedUrl)) {
        const { url, fields } = presignedUrl?.presigned_url;

        const data = new FormData();
        Object.keys(fields).forEach((key) => {
          data.append(key, fields[key]);
        });
        data.append("file", compressed);

        return fetch(url, {
          method: "POST",
          mode: "no-cors",
          body: data
        });
      } else if (checkIsAzure(presignedUrl)) {
        const data = compressed;

        return fetch(presignedUrl?.presigned_url, {
          method: "PUT",
          body: data,
          headers: {
            "Content-Type": "application/octet-stream",
            "x-ms-blob-type": "BlockBlob"
          }
        });
      } else if (checkIsGCP(presignedUrl)) {
        const data = compressed;

        return fetch(presignedUrl?.presigned_url, {
          method: "PUT",
          body: data,
          headers: {
            "Content-Type": "application/octet-stream"
          }
        });
      }
    });
  } else if (file === undefined) {
    return Promise.reject("Error: No file selected");
  } else if (!validateFileExtension(file)) {
    return Promise.reject("Invalid File Type. Please upload a CSV or a gzip file.");
  }

  return Promise.resolve();
}

function getUpload(id) {
  return request(
    baseRequestURL + "v1/data_upload/" + id,
    {
      method: "GET"
    },
    false
  ).then((response) => response);
}

const UploadPanelDetails = ({ account, presigned_url }) => {
  if (checkIsAWS(presigned_url)) {
    const url = presigned_url.presigned_url;

    return (
      <div className={styles.panelBodyItem}>
        <div className={styles.panelBodyItemIcon}>
          <img className={styles.awsIcon} src={AWSIcon} />
        </div>
        <div className={styles.panelBodyItemTextAWS}>
          <div style={{ fontWeight: "bold" }}>Where the data will be uploaded to:</div>
          <div className={styles.panelBodyItemLine}>
            Aible AWS Account Alias: <span title={account.name}>{account.name}</span>
          </div>
          <div className={styles.panelBodyItemLine}>
            S3 URL: <span title={url.url}>{url.url}</span>
          </div>
          <div className={styles.panelBodyItemLine}>
            S3 Bucket: <span title={url.fields.key}>{url.fields.key}</span>
          </div>
        </div>
      </div>
    );
  } else if (checkIsAzure(presigned_url)) {
    const url = new URL(presigned_url?.presigned_url);

    return (
      <div className={styles.panelBodyItem}>
        <div className={styles.panelBodyItemIcon}>
          <img className={styles.awsIcon} src={AzureIcon} />
        </div>
        <div className={styles.panelBodyItemTextAWS}>
          <div style={{ fontWeight: "bold" }}>Where the data will be uploaded to:</div>
          <div className={styles.panelBodyItemLine}>
            Aible Azure Account Alias: <span title={account.name}>{account.name}</span>
          </div>
          <div className={styles.panelBodyItemLine}>
            Origin: <span title={url.origin}>{url.origin}</span>
          </div>
          <div className={styles.panelBodyItemLine}>
            Pathname: <span title={url.pathname}>{url.pathname}</span>
          </div>
        </div>
      </div>
    );
  } else if (checkIsGCP(presigned_url)) {
    const url = new URL(presigned_url?.presigned_url);

    return (
      <div className={styles.panelBodyItem}>
        <div className={styles.panelBodyItemIcon}>
          <img className={styles.awsIcon} src={GCPIcon} />
        </div>
        <div className={styles.panelBodyItemTextAWS}>
          <div style={{ fontWeight: "bold" }}>Where the data will be uploaded to:</div>
          <div className={styles.panelBodyItemLine}>
            Aible GCP Account Alias: <span title={account.name}>{account.name}</span>
          </div>
          <div className={styles.panelBodyItemLine}>
            Origin: <span title={url.origin}>{url.origin}</span>
          </div>
          <div className={styles.panelBodyItemLine}>
            Pathname: <span title={url.pathname}>{url.pathname}</span>
          </div>
        </div>
      </div>
    );
  } else {
    return <React.Fragment />;
  }
};

const UploadPanel = ({ payload, handleUpload, isUploading, uploadDate, showMore, toggleShowMore, errorMessage }) => {
  const expires = moment(payload.expiration_date);

  const linkHasExpired = expires.isAfter() !== true;

  const hasUploaded = uploadDate !== undefined;
  const hasError = errorMessage !== undefined;

  const isAzure = checkIsAzure(payload.presigned_url);
  const isGCP = checkIsGCP(payload.presigned_url);

  const accountType = isAzure ? "Azure" : isGCP ? "GCP" : "AWS";

  return (
    <div className={styles.body}>
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <img className={styles.logoImage} src={Logo} />
          </div>
        </div>
        <div className={styles.panelBody}>
          <div className={styles.panelBodyContent}>
            <div className={styles.panelBodyHeader}>Welcome to the Aible Data Portal</div>
            <div className={styles.panelBodyItem}>
              <div className={styles.panelBodyItemIcon}>
                <FontAwesomeIcon icon={faLock} />
              </div>
              <div className={styles.panelBodyItemText}>
                <span style={{ fontWeight: "bold", marginRight: "3px" }}>Securely upload your data</span> to an
                Aible-linked {accountType} account.
              </div>
            </div>
            <div className={styles.panelBodyItem}>
              <div className={styles.panelBodyItemIcon}>
                <FontAwesomeIcon icon={faHourglass} />
              </div>
              <div className={styles.panelBodyItemText}>
                {linkHasExpired ? (
                  <span style={{ fontWeight: "bold" }}>This link has expired</span>
                ) : (
                  <span>
                    This link will expire in
                    <span style={{ fontWeight: "bold", marginLeft: "3px" }}>{expires.fromNow(true)}</span>
                  </span>
                )}
              </div>
            </div>
            <div className={styles.panelBodyItem}>
              <div className={styles.panelBodyItemIcon}>
                <FontAwesomeIcon icon={faUserCircle} />
              </div>
              <div className={styles.panelBodyItemText}>
                <span style={{ fontWeight: "bold", marginRight: "3px" }}>{payload.created_by.fullname}</span>
                {" created this unique upload URL"}
              </div>
            </div>
            <UploadPanelDetails account={payload.account} presigned_url={payload.presigned_url} />
            <div className={styles.panelBodyItem}>
              <div className={styles.panelBodyItemIcon}>
                <img src={AibleLogo} style={{ width: "100%", height: "auto" }} />
              </div>
              <div className={styles.panelBodyItemTextAWS}>
                <div style={{ fontWeight: "bold" }}>Where the data will be cataloged in Aible:</div>
                <div>{`Organization: ${payload.tenant.name}`}</div>
                <div>{`Folder: ${payload.folder.name}`}</div>
                <div>{`Dataset: ${payload.name}`}</div>
                {payload.description && <span>{`Description: ${payload.description}`}</span>}
              </div>
            </div>
          </div>
          <div className={styles.uploadPanel}>
            <div>
              <div className={styles.uploadPanelHeader}>Upload Your Data</div>
              <div className={styles.uploadPanelSubHeader}>.CSV and .GZ files only</div>
            </div>
            <div>
              <div style={{ display: "none" }}>
                <input type="file" id="fUpload" onChange={(e) => handleUpload(e)} />
              </div>
              {hasUploaded && (
                <div className={styles.uploadSuccess}>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span style={{ marginLeft: "0.5rem" }}>
                    {`Data successfully and securely uploaded ${moment(uploadDate).calendar()}`}
                  </span>
                </div>
              )}
              {hasError && (
                <div className={styles.uploadError}>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  <span style={{ marginLeft: "0.5rem" }}>{errorMessage}</span>
                </div>
              )}
              <div className={styles.uploadButton}>
                <Button
                  type={hasUploaded ? undefined : "primary"}
                  disabled={linkHasExpired || isUploading}
                  onClick={() => document.getElementById("fUpload").click()}
                >
                  <FontAwesomeIcon icon={faLock} />
                  <span style={{ marginLeft: "0.5rem" }}>
                    {linkHasExpired
                      ? "This Upload Link Has Expired"
                      : hasUploaded
                        ? "Securely Upload and Overwrite Data"
                        : "Select Data to Upload Securely"}
                  </span>
                </Button>
                {isUploading && <LoadingIndicator />}
              </div>
              {isAzure ? (
                <div className={styles.uploadPanelSubHeader}>
                  Your data is sent directly to the Azure account using an encrypted connection.{" "}
                  <a onClick={() => toggleShowMore()}>Click Here for Details</a>
                </div>
              ) : isGCP ? (
                <div className={styles.uploadPanelSubHeader}>
                  Your data is sent directly to the GCP account using an encrypted connection.{" "}
                  <a onClick={() => toggleShowMore()}>Click Here for Details</a>
                </div>
              ) : (
                <div className={styles.uploadPanelSubHeader}>
                  Your data is sent directly to the AWS account using an encrypted connection via a presigned url.{" "}
                  <a onClick={() => toggleShowMore()}>Click Here for Details</a>
                </div>
              )}
              {showMore && (
                <div className={styles.uploadPanelSubHeader}>
                  Your data is being uploaded via a presigned url, a standard method for securely uploading data to
                  directly to your cloud infrastructure account. Your data file is transferred directly from your
                  desktop browser to blob storage — S3 if you use AWS, Blob Storage in Azure, or Cloud Storage for GCP —
                  and never passes through Aible servers or any other service. The data is encrypted during transfer
                  with TLS 1.2 ensuring it is completely secure during transmission. Data is encrypted at rest via
                  AES-256. The storage access policy is configured for extremely limited access only by the compute
                  resources for exploratory data analysis (EDA) and automated machine learning algorithms (AutoML)
                  running completely within your cloud infrastructure account. No external access is allowed and Aible
                  monitors your account to ensure these permissions are properly configured for maximum security. AWS
                  S3, Azure Blob Storage, and Google Cloud Storage are recognized for fully secure storage across the
                  industry and meets rigid standards for managing PII data and in systems meeting HIPAA, FedRamp and
                  other regulatory requirements. Aible's unique architecture ensures that your data is only ever stored
                  in your cloud account and is only read by compute resources within that account. The presigned url
                  used during this upload only provides write permissions and has no read capabilities and expires after
                  24 hours. Aible does not ever expose row level data, providing you and your team access only at the
                  aggregate analytics and metadata level, ensuring K-anonymity to prevent reverse engineering of row
                  level data, and flagging data columns which may potentially contain PII. If you are notified during
                  your data upload that your presigned url has expired, notify your Aible contact for a new one.
                  Presigned urls can never be recovered, reused, disassembled or tampered with for any purpose other
                  than their originally intended use for secure point-to-point transfer of data.
                </div>
              )}
              {hasUploaded && (
                <div className={styles.uploadWarning}>
                  <div className={styles.uploadWarningIcon}>
                    <FontAwesomeIcon icon={faExclamationCircle} />
                  </div>
                  <div className={styles.uploadWarningText}>
                    Re-using this upload link before it expires will overwrite the previously uploaded dataset. If you
                    would like to share multiple datasets, request a unique upload link for each dataset from the link
                    creator listed above.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const extractId = (ownProps) =>
  ownProps && ownProps.match && ownProps.match.params && ownProps.match.params.id
    ? ownProps.match.params.id
    : undefined;

export default class UploadPage extends React.Component {
  render() {
    const id = extractId(this.props);

    const state = this.state || {};

    const { isUploading, uploadDate, errorMessage, payload, showMore } = state;

    const handleUpload = (e) => {
      this.setState({ isUploading: true, errorMessage: undefined });
      handleFile(e, payload.presigned_url)
        .then(() =>
          this.setState({
            isUploading: false,
            uploadDate: moment().format()
          })
        )
        .catch((errorMessage) => {
          if (typeof errorMessage === "string" || errorMessage instanceof String) {
            this.setState({
              isUploading: false,
              errorMessage: errorMessage
            });
          } else {
            return Promise.reject(errorMessage);
          }
        });
    };

    if (payload === undefined) {
      getUpload(id).then((payload) => this.setState({ payload }));

      return <LoadingIndicator />;
    }

    return (
      <UploadPanel
        payload={payload}
        handleUpload={handleUpload}
        isUploading={isUploading}
        uploadDate={uploadDate}
        errorMessage={errorMessage}
        showMore={showMore}
        toggleShowMore={() => this.setState({ showMore: !showMore })}
      />
    );
  }
}
